import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLanguage} from "../contexts/LanguageContext";
import {db} from '../config/firebase';
import {Card, Col, Empty, Image, Row, Spin} from 'antd';
import {addDoc, collection, getDocs, limit, orderBy, query, startAfter} from 'firebase/firestore';
import InfiniteScroll from "react-infinite-scroll-component";
import {CaseItem} from "../types/CardItems";

const emptyText = {
    en: {
        description: 'No Cases Found',
        noMore: 'No more cases'
    },
    zh: {
        description: '暂无案例',
        noMore: '没有更多案例了'
    }
};


const initializeCases = async () => {
    try {
        console.log('initializeCases();')
        const casesCollection = collection(db, 'cases');

        for (let i = 1; i <= 100; i++) {
            const caseData = {
                title: {
                    en: `Case Study ${i}`,
                    zh: `案例研究 ${i}`
                },
                client: {
                    en: `Client ${i}`,
                    zh: `客户 ${i}`
                },
                exhibitionArea: Math.floor(Math.random() * 1000) + 100,
                exhibitionLocation: {
                    en: `Location ${i}`,
                    zh: `地点 ${i}`
                },
                images: [
                    `https://picsum.photos/seed/${i}/800/600`,
                    `https://picsum.photos/seed/${i + 1}/800/600`,
                    `https://picsum.photos/seed/${i + 2}/800/600`
                ],
                showTime: new Date().getTime() - (i * 1000), // 确保有序性
                createdAt: new Date().getTime() - (i * 1000)
            };

            await addDoc(casesCollection, caseData);
        }
        console.log('Successfully initialized 100 cases');
    } catch (error) {
        console.error('Error initializing cases:', error);
    }
};

const Cases: React.FC = () => {
    const lang = useLanguage().currentLang;
    const [cases, setCases] = useState<CaseItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const lastDocRef = useRef<any>(null);
    const pageSize = 9;

    const renderContent = () => {
        if (loading && cases.length === 0) {
            return (
                <div className="flex justify-center items-center min-h-[400px]">
                    <Spin size="large"/>
                </div>
            );
        }

        if (!loading && cases.length === 0) {
            return (
                <div className="flex justify-center items-center min-h-[400px]">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={emptyText[lang].description}
                    />
                </div>
            );
        }

        return (
            <div className='mt-20'>
                <InfiniteScroll
                    dataLength={cases.length}
                    next={loadMore}
                    hasMore={hasMore}
                    loader={
                        <div className="flex justify-center py-4">
                            <Spin/>
                        </div>
                    }
                    endMessage={
                        <div className="text-center text-gray-500 py-4">
                            {emptyText[lang].noMore}
                        </div>
                    }
                >
                    <Row gutter={[24, 24]}>
                        {cases.map((item) => (
                            <Col xs={24} sm={12} md={8} key={item.id}>
                                <Card
                                    hoverable
                                    className="h-full"
                                    cover={
                                        <Image.PreviewGroup
                                            items={item.images}
                                        >
                                            <Image
                                                src={item.images[0]}
                                                alt={item.title[lang]}
                                                className="w-full h-64 object-cover"
                                            />
                                        </Image.PreviewGroup>
                                    }>
                                    <Card.Meta
                                        title={item.title[lang]}
                                        description={
                                            <div className="space-y-2">
                                                <p className="text-gray-600">
                                                    {lang === 'en' ? 'Client' : '客户'}: {item.client[lang]}
                                                </p>
                                                <p className="text-gray-600">
                                                    {lang === 'en' ? 'Exhibition Area' : '展览面积'}: {item.exhibitionArea}m²
                                                </p>
                                                <p className="text-gray-600">
                                                    {lang === 'en' ? 'Location' : '展览地点'}: {item.exhibitionLocation[lang]}
                                                </p>
                                            </div>
                                        }
                                    />
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </InfiniteScroll>
            </div>
        );
    };


    const fetchCases = async (isInitial = false) => {
        try {
            setLoading(true);
            let q;

            if (isInitial) {
                q = query(
                    collection(db, 'cases'),
                    orderBy('showTime', 'desc'),
                    limit(pageSize)
                );
            } else {
                if (!lastDocRef.current) {
                    setHasMore(false);
                    return;
                }
                q = query(
                    collection(db, 'cases'),
                    orderBy('showTime', 'desc'),
                    startAfter(lastDocRef.current),
                    limit(pageSize)
                );
            }

            const querySnapshot = await getDocs(q);
            const newCases: CaseItem[] = [];

            querySnapshot.forEach((doc) => {
                newCases.push({
                    id: doc.id,
                    ...doc.data()
                } as CaseItem);
            });

            lastDocRef.current = querySnapshot.docs[querySnapshot.docs.length - 1];

            if (isInitial) {
                setCases(newCases);
            } else {
                setCases(prev => [...prev, ...newCases]);
            }

            setHasMore(querySnapshot.docs.length === pageSize);
        } catch (error) {
            console.error('Error fetching cases:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMore = useCallback(() => {
        if (!loading && hasMore) {
            fetchCases(false);
        }
    }, [loading, hasMore]);


    useEffect(() => {
        fetchCases(true);
    }, []);

    return (
        <div className="container mx-auto px-4 py-8">
            {renderContent()}
        </div>
    );
};

export default Cases;
